import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { usersApi } from '@/api/users'
import type { User, AddMemberData } from '@/types'
import { displayErrorMessage, getErrorMessage, getListItemValidationErrors, getValidationErrors } from '@/utils/error'
import { describeMembersError, getMemberLabel } from '@/components/groups/memberSelection'

interface AddMemberModalProps {
  open: boolean
  existingMemberIds: number[]
  /** Ошибку отправки пробрасывает дальше: модалка сама её показывает */
  onSubmit: (data: AddMemberData) => Promise<void>
  onCancel: () => void
}

export function AddMemberModal({
  open,
  existingMemberIds,
  onSubmit,
  onCancel,
}: AddMemberModalProps) {
  const { t } = useTranslation()
  const [students, setStudents] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedStudents, setSelectedStudents] = useState<User[]>([])
  const [error, setError] = useState<string | null>(null)
  /** Ученики, которых API отклонил при последней отправке */
  const [rejectedIds, setRejectedIds] = useState<number[]>([])

  useEffect(() => {
    if (open) {
      loadStudents()
    } else {
      setSelectedStudents([])
      setError(null)
      setRejectedIds([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const loadStudents = async () => {
    setLoading(true)
    try {
      // Все свои ученики с активным договором (по умолчанию у списка
      // учителя), а не первая страница: в группу можно добавить только их
      const allStudents = await usersApi.listAllStudents()
      setStudents(allStudents.filter((student) => !existingMemberIds.includes(student.id)))
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('groups.errorLoadStudents')))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (selectedStudents.length === 0) {
      setError(t('groups.selectStudents'))
      return
    }

    const submitted = selectedStudents
    try {
      setSubmitting(true)
      setError(null)
      setRejectedIds([])
      await onSubmit({ student_ids: submitted.map((u) => u.id) })
      setSelectedStudents([])
    } catch (err: unknown) {
      const rejected = getListItemValidationErrors(getValidationErrors(err), 'student_ids')
      setRejectedIds(
        (rejected?.indexes ?? [])
          .map((index) => submitted[index]?.id)
          .filter((id): id is number => typeof id === 'number'),
      )
      setError(describeMembersError(err, 'student_ids', submitted, t, 'groups.errorAddMembers'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('groups.addMember')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Autocomplete
          multiple
          options={students}
          getOptionLabel={getMemberLabel}
          value={selectedStudents}
          onChange={(_, newValue) => {
            setSelectedStudents(newValue)
            setRejectedIds((ids) => ids.filter((id) => newValue.some((u) => u.id === id)))
          }}
          loading={loading}
          renderValue={(value, getItemProps) =>
            value.map((student, index) => {
              const { key, ...itemProps } = getItemProps({ index })
              return (
                <Chip
                  key={key}
                  {...itemProps}
                  label={getMemberLabel(student)}
                  color={rejectedIds.includes(student.id) ? 'error' : 'default'}
                />
              )
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('groups.selectStudents')}
              placeholder={t('groups.selectStudentsPlaceholder')}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || selectedStudents.length === 0}
        >
          {submitting ? <CircularProgress size={20} /> : t('groups.addSelected')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
